/**
 * AI Credits: what each coaching action costs, in credits.
 *
 * Decided 2026-10-13 for Miracles Protocol: a member's AI use is governed by
 * credits and nothing is free. Every action that calls Claude spends credits,
 * Master Kala's turns and the automatic daily generations alike; a member at
 * zero gets each generator's built-in template instead and is offered more
 * credits. How many credits an action costs is the tenant's to set
 * (`coaching.credit_costs`, Admin -> Coaching -> Credits); this catalog is the
 * total list of actions with their defaults, so the editor, the server and the
 * app's own credits screen read one definition.
 */

export type CoachingCreditAction =
  | 'kala_message'
  | 'blueprint'
  | 'daily_plan'
  | 'guidance'
  | 'ritual_evening'
  | 'weekly_review'
  | 'journal_reflection'
  | 'affirmation'
  | 'action_suggestions'
  | 'alignment_todos'
  | 'aura'
  | 'karma'
  | 'palmistry';

export type CoachingCreditActionDef = {
  key: CoachingCreditAction;
  label: string;
  /** What triggers it, in the admin's words. */
  what: string;
  defaultCost: number;
};

export const COACHING_CREDIT_ACTIONS: readonly CoachingCreditActionDef[] = [
  {
    key: 'kala_message',
    label: 'Master Kala message',
    what: 'Each reply Kala writes in a chat.',
    defaultCost: 1,
  },
  {
    key: 'blueprint',
    label: 'Success blueprint',
    what: 'The plan written once at the end of onboarding.',
    defaultCost: 1,
  },
  {
    key: 'daily_plan',
    label: 'Daily plan',
    what: 'The day’s priority action, affirmation and insight, once a day.',
    defaultCost: 1,
  },
  {
    key: 'guidance',
    label: 'Daily guidance',
    what: 'The Vedic guidance line, once a day.',
    defaultCost: 1,
  },
  {
    key: 'ritual_evening',
    label: 'Evening review',
    what: 'The written evening summary, once a day.',
    defaultCost: 1,
  },
  {
    key: 'weekly_review',
    label: 'Weekly review',
    what: 'The written weekly summary, once a week.',
    defaultCost: 1,
  },
  {
    key: 'journal_reflection',
    label: 'Journal reflection',
    what: 'A reflection on one journal entry, on request.',
    defaultCost: 1,
  },
  {
    key: 'affirmation',
    label: 'Affirmation',
    what: 'An affirmation written for the member, on request.',
    defaultCost: 1,
  },
  {
    key: 'action_suggestions',
    label: 'Action suggestions',
    what: 'Suggested actions for a goal, on request.',
    defaultCost: 1,
  },
  {
    key: 'alignment_todos',
    label: 'Alignment to-dos',
    what: 'The law-of-attraction to-dos, once a day.',
    defaultCost: 1,
  },
  { key: 'aura', label: 'Aura reading', what: 'An aura reading, on request.', defaultCost: 1 },
  { key: 'karma', label: 'Karma reading', what: 'A karma reading, on request.', defaultCost: 1 },
  {
    key: 'palmistry',
    label: 'Palm reading',
    what: 'A palm reading from a photo, on request.',
    defaultCost: 1,
  },
];

export const COACHING_CREDIT_ACTION_KEYS: readonly CoachingCreditAction[] =
  COACHING_CREDIT_ACTIONS.map((a) => a.key);

/** Tenant overrides by action; a missing key means the default, 0 means free. */
export type CoachingCreditCosts = Partial<Record<CoachingCreditAction, number>>;

/** The largest cost the editor accepts; keeps a typo from locking every member out. */
export const COACHING_CREDIT_COST_MAX = 100;

/** How many credits `action` costs under `costs`: the override, else the default. */
export function creditCost(
  costs: CoachingCreditCosts | null | undefined,
  action: CoachingCreditAction,
): number {
  const override = costs?.[action];
  if (typeof override === 'number' && Number.isFinite(override) && override >= 0) {
    return Math.min(Math.floor(override), COACHING_CREDIT_COST_MAX);
  }
  return COACHING_CREDIT_ACTIONS.find((a) => a.key === action)?.defaultCost ?? 1;
}

/** The whole table as the admin editor and the app's credits screen show it. */
export function resolveCreditCosts(
  costs: CoachingCreditCosts | null | undefined,
): Array<CoachingCreditActionDef & { cost: number }> {
  return COACHING_CREDIT_ACTIONS.map((a) => ({ ...a, cost: creditCost(costs, a.key) }));
}
